import { t } from 'i18next';
import type { TemplateSave } from '@morph-mapper/types';
import { useStore } from '@/store';
import { useNotify, useReevaluate } from '@/hooks';
import { ROOT_KEY } from '@/config';
import { fetchSchemaById } from '../../../../api/schemas/crud';
import { listEvaluableFields } from '../../../../utils/reevaluation';
import { useTemplateBuilder } from '../../../setup/hooks';

type Save = Omit<TemplateSave, 'id'>;

/** `?templateCreated=` as sent by POST /AnalyzeTemplate; anything else is "not sent". */
export const parseTemplateCreated = (value: string | null) =>
  value === 'true' ? true : value === 'false' ? false : undefined;

const hasEntriesTree = (save: Save) =>
  Object.values(save.entries ?? {}).some((entry) => entry.name === ROOT_KEY);

/**
 * What useTemplateRoute needs while loading: an entries tree for templates
 * the backend (AnalyzeTemplate) created without one.
 */
export const useEntriesTree = (operatorId: number) => {
  const { buildTemplate } = useTemplateBuilder(operatorId);
  const { error } = useNotify();

  /**
   * The save itself when it has an entries tree; otherwise the save with a
   * tree built from its schema, the way the create-template modal builds one.
   * Undefined (after a notification) when the tree cannot be built.
   */
  const withEntriesTree = async (save: Save): Promise<Save | undefined> => {
    if (hasEntriesTree(save)) return save;

    try {
      const built = buildTemplate(
        save.name,
        await fetchSchemaById(save.schemaId),
      );
      if (!built)
        throw new Error(`No entries built for schema ${save.schemaId}`);

      return {
        ...save,
        entries: built.entries,
        config: {
          ...save.config,
          preconfiguration: built.config.preconfiguration,
          updateDiscardIds: built.config.updateDiscardIds,
        },
      };
    } catch {
      error(t('message.error.templateCreationFailed'));
      return undefined;
    }
  };

  return { withEntriesTree };
};

/**
 * The follow-up the booking UI asked for with `templateCreated`. Only usable
 * once the template is loaded: useReevaluate reads the variant and text
 * content at render time, and both throw while unset.
 */
export const useArrivalAction = () => {
  const { reevaluate } = useReevaluate();
  const setReevaluateOpen = useStore(({ ai }) => ai.setReevaluateOpen);
  const { error } = useNotify();

  /**
   * A new template opens the re-evaluate dialog so the user picks plugins and
   * settings; an existing one runs its assigned plugins on every field.
   */
  const actOnArrival = async (templateCreated: boolean) => {
    if (templateCreated) {
      setReevaluateOpen(true);
      return;
    }

    const { entries, ai } = useStore.getState();
    const fieldIds = listEvaluableFields(
      entries.getEntries(),
      entries.hasValue,
      ai.staleFields,
    ).map((field) => field.id);

    try {
      await reevaluate(fieldIds);
    } catch (e) {
      error(
        t('reevaluate.failed', {
          message: e instanceof Error ? e.message : String(e),
        }),
      );
    }
  };

  return { actOnArrival };
};
